import { useState, useEffect, useCallback, useRef } from 'react'
import { BreathingConfig, BreathingState, BreathingSessionData } from '@/types/breathing'

export function useBreathingSession(config: BreathingConfig) {
  const [sessionData, setSessionData] = useState<BreathingSessionData>({
    startTime: 0,
    endTime: 0,
    duration: config.duration,
    isActive: false
  })

  const [breathingState, setBreathingState] = useState<BreathingState>({
    isInhaling: true,
    progress: 0,
    timeRemaining: config.duration * 60,
    cycleProgress: 0
  })

  const animationFrameRef = useRef<number | undefined>(undefined)
  const startTimeRef = useRef<number>(0)

  const startSession = useCallback(() => {
    const now = Date.now()
    const duration = config.duration * 60 * 1000 // Convert to milliseconds
    
    // We'll set the actual start time when the first animation frame runs
    startTimeRef.current = 0
    
    setSessionData({
      startTime: now,
      endTime: now + duration,
      duration: config.duration,
      isActive: true
    })

    setBreathingState(prev => ({
      ...prev,
      timeRemaining: config.duration * 60,
      progress: 0
    }))
  }, [config.duration])

  const stopSession = useCallback(() => {
    setSessionData(prev => ({ ...prev, isActive: false }))
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }
  }, [])

  const updateBreathingCycle = useCallback((currentTime: number) => {
    if (!sessionData.isActive) return

    // Set start time on first frame
    if (startTimeRef.current === 0) {
      startTimeRef.current = currentTime
    }

    const elapsed = (currentTime - startTimeRef.current) / 1000 // Convert to seconds
    const totalDuration = config.duration * 60
    
    console.log('Progress debug:', {
      currentTime,
      startTime: startTimeRef.current,
      elapsed,
      totalDuration,
      sessionProgress: elapsed / totalDuration
    })
    
    if (elapsed >= totalDuration) {
      stopSession()
      return
    }

    const timeRemaining = totalDuration - elapsed
    const sessionProgress = Math.max(0, Math.min(1, elapsed / totalDuration)) // Clamp between 0 and 1

    // Breathing cycle: inhale for 4s, exhale for 6s (10s total)
    const cycleTime = (currentTime / 1000) % 10
    const isInhaling = cycleTime < 4
    const cycleProgress = isInhaling ? cycleTime / 4 : (cycleTime - 4) / 6

    setBreathingState({
      isInhaling,
      progress: sessionProgress,
      timeRemaining,
      cycleProgress
    })
  }, [sessionData.isActive, config.duration, stopSession])

  useEffect(() => {
    if (!sessionData.isActive) return

    const animate = (currentTime: number) => {
      updateBreathingCycle(currentTime)
      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animationFrameRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [sessionData.isActive, updateBreathingCycle])

  return {
    sessionData,
    breathingState,
    startSession,
    stopSession
  }
}
